import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';

export type IconBadgeColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export type IconBadgeSize = 'sm' | 'md' | 'lg';

const SIZE_PX: Record<IconBadgeSize, { box: number; icon: number; radius: number }> = {
  sm: { box: 32, icon: 18, radius: 8 },
  md: { box: 40, icon: 22, radius: 10 },
  lg: { box: 48, icon: 26, radius: 12 },
};

export function IconBadge({
  icon,
  color = 'primary',
  size = 'md',
  filled = false,
  className,
}: {
  icon: ReactNode;
  color?: IconBadgeColor;
  size?: IconBadgeSize;
  filled?: boolean;
  className?: string;
}): JSX.Element {
  const theme = useTheme();
  const { box, icon: iconSize, radius } = SIZE_PX[size];
  const palette = theme.palette[color];
  const main = palette.main;
  const contrast = palette.contrastText ?? '#fff';

  return (
    <Box
      className={className}
      sx={{
        width: box,
        height: box,
        minWidth: box,
        borderRadius: `${radius}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: filled ? main : alpha(main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
        color: filled ? contrast : main,
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '& > svg': { fontSize: iconSize },
      }}
      aria-hidden
    >
      {icon}
    </Box>
  );
}
